import type { HTMLAttributes, ReactNode, ElementType } from 'react'
import { cn } from '@/lib/utils'

type SectionTone = 'default' | 'muted' | 'dark' | 'brand'
type SectionSpacing = 'sm' | 'md' | 'lg'
type SectionWidth = 'default' | 'narrow' | 'wide' | 'full'

interface SectionProps extends HTMLAttributes<HTMLElement> {
  id?: string
  as?: ElementType
  tone?: SectionTone
  spacing?: SectionSpacing
  width?: SectionWidth
  containerClassName?: string
  eyebrow?: string
  title?: string
  lead?: string
  /**
   * When true the title/lead/eyebrow header is aligned center. Defaults
   * to left-aligned which is the disciplined, editorial default for
   * this industrial brand.
   */
  headerCenter?: boolean
  children: ReactNode
}

const toneClasses: Record<SectionTone, string> = {
  default: 'bg-ink-100 text-ink-700',
  muted: 'bg-white text-ink-700',
  dark: 'bg-ink-900 text-white',
  brand: 'bg-brand-600 text-white',
}

const spacingClasses: Record<SectionSpacing, string> = {
  sm: 'py-12 md:py-16',
  md: 'py-16 md:py-20',
  lg: 'py-20 md:py-24',
}

const widthClasses: Record<SectionWidth, string> = {
  narrow: 'max-w-4xl',
  default: 'max-w-7xl',
  wide: 'max-w-[88rem]',
  full: 'max-w-none',
}

/**
 * Section — the canonical content slab used across every page.
 * Enforces the project's vertical rhythm (py-16→py-24) and 7xl max-width.
 */
export function Section({
  id,
  as: Component = 'section',
  tone = 'default',
  spacing = 'lg',
  width = 'default',
  className,
  containerClassName,
  eyebrow,
  title,
  lead,
  headerCenter = false,
  children,
  ...rest
}: SectionProps) {
  const hasHeader = Boolean(eyebrow || title || lead)
  const isDarkTone = tone === 'dark' || tone === 'brand'

  return (
    <Component
      id={id}
      className={cn(
        'relative',
        toneClasses[tone],
        spacingClasses[spacing],
        className
      )}
      {...rest}
    >
      <div
        className={cn(
          'mx-auto px-4 sm:px-6 lg:px-8',
          widthClasses[width],
          containerClassName
        )}
      >
        {hasHeader && (
          <header
            className={cn(
              'mb-10 md:mb-14 max-w-3xl',
              headerCenter && 'mx-auto text-center'
            )}
          >
            {eyebrow && (
              <p
                className={cn(
                  'mb-3 text-[11px] font-bold uppercase tracking-[0.22em]',
                  isDarkTone ? 'text-brand-300' : 'text-brand-600',
                  headerCenter && 'flex items-center justify-center gap-3 before:block before:h-px before:w-8 before:bg-current after:block after:h-px after:w-8 after:bg-current'
                )}
              >
                {eyebrow}
              </p>
            )}
            {title && (
              <h2
                className={cn(
                  'fx-text-balance font-bold leading-tight',
                  'text-2xl sm:text-3xl md:text-[2rem]',
                  isDarkTone ? 'text-white' : 'text-ink-900'
                )}
              >
                {title}
              </h2>
            )}
            {lead && (
              <p
                className={cn(
                  'mt-4 fx-text-pretty text-base md:text-lg leading-relaxed',
                  isDarkTone ? 'text-white/80' : 'text-ink-600'
                )}
              >
                {lead}
              </p>
            )}
          </header>
        )}
        {children}
      </div>
    </Component>
  )
}
